import { DetailedOrder } from '../domain/types';
import { COLLECTIONS } from '../domain/collections';
import { db } from '../infrastructure/firebaseConfig';
import { logger } from '../infrastructure/logger';
import { typedStorage } from '../utils/storage';
import { CacheInvalidator } from '../utils/cacheInvalidation';
import { PAGINATION } from '../domain/constants';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  deleteDoc
} from "firebase/firestore";
import { BaseRepository, BaseEntity } from '../domain/services/BaseRepository';

interface OrderEntity extends DetailedOrder, BaseEntity {}

class OrderMutations extends BaseRepository<OrderEntity> {
  protected collectionName = COLLECTIONS.ORDERS;

  saveGuestOrderId(orderId: string) {
    const existing = typedStorage.guestOrderIds.get();
    if (!existing.includes(orderId)) {
      typedStorage.guestOrderIds.set([...existing, orderId]);
    }
  }

  async cancelOrder(id: string): Promise<void> {
    const docRef = doc(db, this.collectionName, id);
    await updateDoc(docRef, {
      status: 'rejected',
      statusLabel: 'Cancelado',
      canCancel: false,
      isArchived: true
    });
    CacheInvalidator.invalidateOrder(id);
  }

  async toggleArchive(id: string, isArchived: boolean): Promise<void> {
    const docRef = doc(db, this.collectionName, id);
    await updateDoc(docRef, { isArchived });
    CacheInvalidator.invalidateOrder(id);
  }

  async updateStatus(id: string, status: string): Promise<void> {
    const docRef = doc(db, this.collectionName, id);

    let label = 'Pendiente';
    let archived = false;

    switch(status) {
      case 'pending': label = 'Pendiente de Pago'; break;
      case 'approved': label = 'Preparando'; break;
      case 'shipped': label = 'En camino'; break;
      case 'delivered': label = 'Entregado'; break;
      case 'rejected': label = 'Cancelado'; archived = true; break;
    }

    await updateDoc(docRef, {
      status: status,
      statusLabel: label,
      isArchived: archived
    });

    CacheInvalidator.invalidateOrder(id);
  }

  async createOrder(newOrder: DetailedOrder): Promise<void> {
    if (newOrder.id) {
      await setDoc(doc(db, this.collectionName, newOrder.id), newOrder);
      CacheInvalidator.invalidateOrder(newOrder.id);
    } else {
      const docRef = await addDoc(collection(db, this.collectionName), newOrder);
      CacheInvalidator.invalidateOrder(docRef.id);
    }
  }

  async deleteAllOrders(): Promise<void> {
    try {
      const snapshot = await getDocs(collection(db, this.collectionName));
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
    } catch (error) {
      logger.error('[OrderMutations] Error deleting all orders', error);
      throw error;
    }
  }

  async updatePartnerOrderStatus(orderId: string, status: string, partnerId: string, notes?: string): Promise<boolean> {
    try {
      const orderRef = doc(db, this.collectionName, orderId);
      const orderSnap = await getDocs(query(collection(db, this.collectionName), where("__name__", "==", orderId)));
      
      if (orderSnap.empty) {
        logger.error("Order not found", new Error("Order not found"));
        return false;
      }

      const order = { ...orderSnap.docs[0].data(), id: orderSnap.docs[0].id } as DetailedOrder;

      if (order.partnerId !== partnerId) {
        logger.error("Order not found or partner not authorized", new Error("Authorization failed"));
        return false;
      }

      const validTransitions: Record<string, string[]> = {
        'pending': ['preparing', 'rejected'],
        'preparing': ['shipped', 'rejected'],
        'shipped': ['delivered'],
        'delivered': [],
        'rejected': []
      };

      const currentStatus = (order as any).partnerStatus || 'pending';
      if (!validTransitions[currentStatus]?.includes(status)) {
        logger.error(`Invalid status transition from ${currentStatus} to ${status}`, new Error("Invalid state transition"));
        return false;
      }

      const statusLabels: Record<string, string> = {
        'pending': 'Pendiente',
        'preparing': 'Preparando',
        'shipped': 'Enviado',
        'delivered': 'Entregado',
        'rejected': 'Cancelado'
      };

      type OrderPartnerStatusUpdate = {
        partnerStatus: string;
        updatedAt: string;
        timeline?: Array<{ status: string; label: string; isCompleted: boolean; date?: string }>;
        partnerNotes?: string;
        status?: string;
        statusLabel?: string;
        deliveredAt?: string;
        isArchived?: boolean;
      };

      const updateData: OrderPartnerStatusUpdate = {
        partnerStatus: status,
        updatedAt: new Date().toISOString()
      };

      const timelineEntry: { status: string; label: string; isCompleted: boolean; date: string } = {
        status: status,
        label: statusLabels[status] || status,
        isCompleted: true,
        date: new Date().toISOString()
      };

      if (order.timeline) {
        updateData.timeline = [...order.timeline, timelineEntry];
      } else {
        updateData.timeline = [timelineEntry];
      }

      if (notes) {
        updateData.partnerNotes = notes;
      }

      if (status === 'delivered') {
        updateData.status = 'delivered';
        updateData.statusLabel = 'Entregado';
        updateData.deliveredAt = new Date().toISOString();
      }

      if (status === 'rejected') {
        updateData.status = 'rejected';
        updateData.statusLabel = 'Cancelado';
        updateData.isArchived = true;
      }

      await updateDoc(orderRef, updateData);
      CacheInvalidator.invalidateOrder(orderId);

      return true;
    } catch (error) {
      logger.error('[OrderMutations] Error updating partner order status', error);
      return false;
    }
  }

  async assignPartnerToOrder(orderId: string, partnerId: string): Promise<boolean> {
    try {
      const docRef = doc(db, this.collectionName, orderId);
      await updateDoc(docRef, {
        partnerId: partnerId,
        assignedAt: new Date().toISOString()
      });

      CacheInvalidator.invalidateOrder(orderId);

      return true;
    } catch (error) {
      logger.error('[OrderMutations] Error assigning partner to order', error);
      return false;
    }
  }

  async linkGuestOrdersToUser(email: string, dni: string, userId: string): Promise<string[]> {
    try {
      const emailQuery = query(
        collection(db, this.collectionName),
        where("userId", "==", "guest"),
        where("email", "==", email),
        limit(PAGINATION.LINK_ORDERS_LIMIT)
      );

      const emailSnapshot = await getDocs(emailQuery);
      const ordersToLink = new Map<string, DetailedOrder>();

      emailSnapshot.docs.forEach(doc => {
        ordersToLink.set(doc.id, { ...doc.data(), id: doc.id } as DetailedOrder);
      });

      if (dni) {
        const dniQuery = query(
          collection(db, this.collectionName),
          where("userId", "==", "guest"),
          where("guestInfo.dni", "==", dni),
          limit(PAGINATION.LINK_ORDERS_LIMIT)
        );

        const dniSnapshot = await getDocs(dniQuery);
        dniSnapshot.docs.forEach(doc => {
          if (!ordersToLink.has(doc.id)) {
            ordersToLink.set(doc.id, { ...doc.data(), id: doc.id } as DetailedOrder);
          }
        });
      }

      const linkedOrderIds: string[] = [];
      const updatePromises = Array.from(ordersToLink.entries()).map(async ([orderId, _order]) => {
        const orderRef = doc(db, this.collectionName, orderId);
        await updateDoc(orderRef, {
          userId: userId,
          customerType: 'verified',
          linkedAt: new Date().toISOString()
        });
        linkedOrderIds.push(orderId);
        CacheInvalidator.invalidateOrder(orderId);
      });

      await Promise.all(updatePromises);

      typedStorage.guestOrderIds.remove();

      return linkedOrderIds;
    } catch (error) {
      logger.error('[OrderMutations] Error linking guest orders', error);
      return [];
    }
  }
}

export const orderMutations = new OrderMutations();
